import { Chart, registerables } from 'chart.js';
import { api } from '../api.js';
import { colorForProject } from '../utils/colors.js';
import { showNotification } from '../components/notification.js';

Chart.register(...registerables);

const PERIODS = [
  { weeks: 1,  label: '1 нед' },
  { weeks: 2,  label: '2 нед' },
  { weeks: 4,  label: '4 нед' },
  { weeks: 8,  label: '8 нед' },
  { weeks: 12, label: '12 нед' },
];

let _chart = null;

export async function initResources(container) {
  container.style.cssText = 'height:100%; display:flex; flex-direction:column; overflow:hidden;';
  container.innerHTML = `
    <div class="gantt-toolbar res-toolbar">
      <span class="toolbar-label">Ресурс:</span>
      <select class="res-resource"></select>
      <span class="toolbar-label">Период:</span>
      <select class="res-period">
        ${PERIODS.map(p => `<option value="${p.weeks}"${p.weeks === 4 ? ' selected' : ''}>${p.label}</option>`).join('')}
      </select>
      <button class="res-refresh scale-btn" type="button">↻ Обновить</button>
      <span class="saving-indicator" hidden>Загрузка…</span>
    </div>
    <div class="res-chart-host" style="flex:1; min-height:0; padding: var(--space-3) var(--space-4);">
      <canvas id="res-canvas"></canvas>
    </div>
    <div class="res-detail" hidden></div>
  `;

  const resourceSelect = container.querySelector('.res-resource');
  const periodSelect   = container.querySelector('.res-period');
  const refreshBtn     = container.querySelector('.res-refresh');
  const savingEl       = container.querySelector('.saving-indicator');
  const detailEl       = container.querySelector('.res-detail');

  // Populate resource selector. Default to Ruslan (id=1).
  let resources = [];
  try {
    resources = await api.resources();
  } catch (e) {
    if (e.message === '401') return;
    showNotification('Не удалось загрузить ресурсы', 'error');
    return;
  }
  resourceSelect.innerHTML = resources
    .map(r => `<option value="${r.id}"${r.id === 1 ? ' selected' : ''}>${escHtml(r.name)}</option>`)
    .join('');

  async function reload() {
    const resourceId = Number(resourceSelect.value);
    const weeks      = Number(periodSelect.value);
    savingEl.hidden = false;
    detailEl.hidden = true;
    try {
      await renderHistogram(resourceId, weeks, detailEl);
    } catch (e) {
      if (e.message === '401') return;
      showNotification('Ошибка загрузки workload: ' + e.message, 'error');
    } finally {
      savingEl.hidden = true;
    }
  }

  resourceSelect.addEventListener('change', reload);
  periodSelect  .addEventListener('change', reload);
  refreshBtn    .addEventListener('click',  reload);

  await reload();
}

async function renderHistogram(resourceId, weeks, detailEl) {
  const today = new Date();
  const start = new Date(today); start.setDate(start.getDate() - 1);    // include yesterday
  const end   = new Date(today); end  .setDate(end  .getDate() + weeks * 7);

  const startISO = isoDate(start);
  const endISO   = isoDate(end);

  const rows = await api.resourceWorkload(resourceId, startISO, endISO);

  // Build per-project datasets: { projectName: [minutes per day] }
  // Also keep a parallel array of day rows for click handling.
  const labels = rows.map(r => r.work_date);
  const projectSeries = new Map(); // name → { id, color, data:[..n] }

  rows.forEach((r, dayIdx) => {
    const breakdown = r.project_breakdown ?? [];
    // Aggregate per-day minutes per project for this day.
    // breakdown items carry `daily_minutes` (full-task minutes / duration) since
    // migration 011; the frontend sums those to get the bar height for the day.
    const perProject = new Map();
    for (const item of breakdown) {
      const key = item.project_id ?? item.project_name;
      const minutes = Number(item.daily_minutes ?? item.minutes ?? 0);
      const prev = perProject.get(key) ?? { minutes: 0, name: item.project_name };
      perProject.set(key, { minutes: prev.minutes + minutes, name: item.project_name });
    }
    for (const [key, agg] of perProject) {
      if (!projectSeries.has(key)) {
        projectSeries.set(key, {
          name: agg.name,
          color: typeof key === 'number' ? colorForProject(key) : colorForProject(hashString(agg.name)),
          data: new Array(rows.length).fill(0),
        });
      }
      projectSeries.get(key).data[dayIdx] = agg.minutes;
    }
  });

  const datasets = [...projectSeries.values()].map(p => ({
    label: p.name,
    data: p.data,
    backgroundColor: p.color,
    borderColor: 'rgba(0,0,0,0.25)',
    borderWidth: 1,
    stack: 'work',
    type: 'bar',
  }));

  // Capacity line — varies per day.
  datasets.push({
    label: 'Capacity',
    data: rows.map(r => r.capacity_minutes ?? 0),
    type: 'line',
    borderColor: '#f85149',
    backgroundColor: 'transparent',
    borderWidth: 2,
    pointRadius: 0,
    stepped: 'middle',
    fill: false,
    order: 0, // draw on top
  });

  // Overload tag — invisible dataset whose only role is to color the day label red.
  // We add overload info to the tooltip via beforeBody hook.
  const overloadByDay = rows.map(r => r.overload_minutes ?? 0);

  if (_chart) _chart.destroy();
  const canvas = document.getElementById('res-canvas');
  _chart = new Chart(canvas, {
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 200 },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { color: '#c9d1d9', boxWidth: 12 } },
        title: {
          display: true,
          text: `Загрузка ресурса (${weeks} нед.) — минуты в день`,
          color: '#c9d1d9',
          font: { size: 14, weight: '500' },
        },
        tooltip: {
          callbacks: {
            beforeBody: (items) => {
              const idx = items[0]?.dataIndex;
              if (idx == null) return '';
              const cap = rows[idx].capacity_minutes ?? 0;
              const planned = rows[idx].planned_minutes ?? 0;
              const ovr = overloadByDay[idx];
              const lines = [`Capacity: ${cap} мин`, `Planned: ${planned} мин`];
              if (ovr > 0) lines.push(`⚠ Overload: ${ovr} мин`);
              return lines;
            },
            label: (ctx) => {
              if (ctx.dataset.label === 'Capacity') return null; // already in beforeBody
              const v = ctx.parsed.y;
              return v ? `${ctx.dataset.label}: ${Math.round(v)} мин` : null;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: { color: '#8b949e', maxRotation: 45, autoSkip: true, autoSkipPadding: 12 },
          grid: { color: 'rgba(139,148,158,0.08)' },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { color: '#8b949e' },
          grid: { color: 'rgba(139,148,158,0.08)' },
          title: { display: true, text: 'минут', color: '#8b949e' },
        },
      },
      onClick: (_evt, elements) => {
        if (!elements.length) return;
        const idx = elements[0].index;
        showDayDetail(rows[idx], detailEl);
      },
    },
  });
}

function showDayDetail(row, el) {
  const items = row.project_breakdown ?? [];
  if (!items.length) {
    el.hidden = true;
    return;
  }
  el.innerHTML = `
    <h3>${row.work_date} — ${items.length} задач, ${row.planned_minutes} мин из ${row.capacity_minutes}</h3>
    ${row.overload_minutes > 0 ? `<p class="res-overload">⚠ Перегрузка на ${row.overload_minutes} мин</p>` : ''}
    <ul>
      ${items.map(i => `
        <li>
          <span class="res-detail-fever ${i.fever_zone || 'green'}"></span>
          <strong>${escHtml(i.project_name)}</strong> — ${escHtml(i.task_name)}
          <span class="res-detail-min">${i.daily_minutes ?? i.minutes ?? 0} мин/день</span>
        </li>
      `).join('')}
    </ul>
  `;
  el.hidden = false;
}

function isoDate(d) {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

// FNV-1a-ish hash for stable project_name → number color seed.
function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

function escHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
